import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Header, Groupper, Table, Label, Icon, Placeholder, Toolbar, Modal, Dropdown, Button, Message, Input } from 'react-frontier';
import { useBreadcrumbs, useUser } from '../Hooks';
import { bindClick, formatUnix, getStatusColor } from '../SiteUtil';
import { ProspectSource, PromoterStatus, SetLoading, UserClass } from '../SiteClasses';
import UserAccess from '../UserAccess';
import Comments from '../components/Comments';
import Activities from '../components/Activities';
import PromoterForm from '../components/PromoterForm';
import Toast from '../Toast';
import API from '../API';

enum Modals{
	STATUS = 1,
	CONVERT = 2,
	ASSIGN = 3,
	EDIT = 4,
	DELETE = 5,
}

var Promoter = ()=>{
	var params = useParams<{ prospect_id?: string, promoter_id?: string }>();
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var navigate = useNavigate();
	var isProspect = !!params.prospect_id;
	var id = parseInt(params.prospect_id || params.promoter_id);
	var target = isProspect ? { prospect_id: id } : { promoter_id: id };
	var canEdit = hasAccess(isProspect ? UserAccess.PROSPECTS.EDIT_PROSPECTS : UserAccess.PROMOTERS.EDIT_PROMOTERS);
	var canDelete = hasAccess(isProspect ? UserAccess.PROSPECTS.DELETE_PROSPECTS : UserAccess.PROMOTERS.DELETE_PROMOTERS);
	var canConvert = hasAccess(UserAccess.PROMOTERS.EDIT_PROMOTERS);

	var [info, setInfo] = useState<any>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [shownModal, setShownModal] = useState<number>(null);
	var [statuses, setStatuses] = useState<PromoterStatus[]>(null);
	var [selectedStatus, setSelectedStatus] = useState<number>(null);
	var [users, setUsers] = useState<UserClass[]>(null);
	var [selectedUser, setSelectedUser] = useState<number>(null);
	var [prompts, setPrompts] = useState<string[]>(null);
	var [editName, setEditName] = useState<string>('');
	var [linkSearch, setLinkSearch] = useState<string>('');
	var [linkResults, setLinkResults] = useState<{ promoter_id: number, promoter_name: string }[]>(null);
	var [selectedPromoter, setSelectedPromoter] = useState<number>(null);
	var [refreshing, setRefreshing] = useState<boolean>(false);

	useEffect(()=>{
		setInfo(null);
		setLoadError(null);
		var fn = isProspect ? API.getProspect : API.getPromoterInfo;
		fn(id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setInfo(res.data);
		}).catch(()=>{
			setLoadError('Hubo un error cargando la información. (LCL-1)');
		});
	}, [id, isProspect]);

	useEffect(()=>{
		setTitle(isProspect ? `Prospecto #${id}` : `Promotor #${id}`);
	}, [id, isProspect]);

	useEffect(()=>{
		if(shownModal!==Modals.CONVERT) return;
		var run = ()=>{
			setLinkResults(null);
			API.searchUnlinkedPromoters(linkSearch.trim() || undefined).then(res=>{
				if(res.error) return setPrompts([res.message]);
				setLinkResults(res.data || []);
			}).catch(()=>{
				setPrompts(['Hubo un error buscando promotores. (LCL-1)']);
			});
		};
		var t = setTimeout(run, 300);
		return ()=>clearTimeout(t);
	}, [shownModal, linkSearch]);

	var openStatus = ()=>{
		setPrompts(null);
		setSelectedStatus(info?.status_id ?? null);
		setShownModal(Modals.STATUS);
		if(!statuses){
			API.getStatuses().then(res=>{
				if(res.error) return Toast.error(res.message);
				setStatuses(res.data || []);
			}).catch(()=>Toast.error('Hubo un error cargando los estatus. (LCL-1)'));
		}
	}

	var saveStatus = (setLoading: SetLoading)=>{
		if(selectedStatus==null) return setPrompts(['Selecciona un estatus']);
		setLoading(true);
		API.setRecordStatus(target, selectedStatus).then(res=>{
			if(res.error) return setPrompts([res.message]);
			var st = statuses?.find(s=>s.status_id===selectedStatus);
			setInfo((prev: any)=>({ ...prev, status_id: selectedStatus, status_name: st?.status_name || prev.status_name }));
			Toast.success('Estatus actualizado');
			setShownModal(null);
		}).catch(()=>{
			setPrompts(['Hubo un error actualizando el estatus. (LCL-1)']);
		}).finally(()=>setLoading(false));
	}

	var openAssign = ()=>{
		setPrompts(null);
		setSelectedUser(info?.user_assigned ?? null);
		setShownModal(Modals.ASSIGN);
		if(!users){
			API.getUsers(200, 0).then(res=>{
				if(res.error) return Toast.error(res.message);
				setUsers(res.data || []);
			}).catch(()=>Toast.error('Hubo un error cargando los usuarios. (LCL-1)'));
		}
	}

	var saveAssign = (setLoading: SetLoading)=>{
		setLoading(true);
		API.assignRecord(target, selectedUser ?? null).then(res=>{
			if(res.error) return setPrompts([res.message]);
			var u = users?.find(x=>x.user_id===selectedUser);
			setInfo((prev: any)=>({ ...prev, user_assigned: selectedUser ?? null, user_assigned_name: u ? `${u.first_name} ${u.last_name}` : null }));
			Toast.success(selectedUser ? 'Registro asignado' : 'Asignación removida');
			setShownModal(null);
		}).catch(()=>{
			setPrompts(['Hubo un error asignando el registro. (LCL-1)']);
		}).finally(()=>setLoading(false));
	}

	var openConvert = ()=>{
		setPrompts(null);
		setLinkSearch('');
		setLinkResults(null);
		setSelectedPromoter(null);
		setShownModal(Modals.CONVERT);
	}

	var convert = (setLoading: SetLoading)=>{
		if(selectedPromoter==null) return setPrompts(['Selecciona un promotor para vincular.']);
		setLoading(true);
		API.convertProspect(id, selectedPromoter).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success('Prospecto vinculado a promotor');
			setShownModal(null);
			navigate(`/promoters/${res.data.promoter_id}`);
		}).catch(()=>{
			setPrompts(['Hubo un error vinculando el prospecto. (LCL-1)']);
		}).finally(()=>setLoading(false));
	}

	var refreshPromoters = ()=>{
		setPrompts(null);
		setRefreshing(true);
		API.syncPromoters().then(res=>{
			if(res.error){
				setRefreshing(false);
				return setPrompts([res.message]);
			}
			return API.searchUnlinkedPromoters(linkSearch.trim() || undefined).then(r=>{
				setRefreshing(false);
				if(r.error) return setPrompts([r.message]);
				setLinkResults(r.data || []);
				Toast.success('Promotores actualizados');
			});
		}).catch(()=>{
			setRefreshing(false);
			setPrompts(['Hubo un error actualizando los promotores. (LCL-2)']);
		});
	}

	var openEdit = ()=>{
		setPrompts(null);
		setEditName((isProspect ? info?.prospect_name : info?.promoter_name) || '');
		setShownModal(Modals.EDIT);
	}

	var saveName = (setLoading: SetLoading)=>{
		if(!editName || editName.trim().length<2) return setPrompts(['El nombre no es válido.']);
		setLoading(true);
		API.renameRecord(target, editName.trim()).then(res=>{
			if(res.error) return setPrompts([res.message]);
			setInfo((prev: any)=>({ ...prev, [isProspect ? 'prospect_name' : 'promoter_name']: editName.trim() }));
			Toast.success('Nombre actualizado');
			setShownModal(null);
		}).catch(()=>{
			setPrompts(['Hubo un error actualizando el nombre. (LCL-1)']);
		}).finally(()=>setLoading(false));
	}

	var removeRecord = (setLoading: SetLoading)=>{
		setLoading(true);
		API.deleteRecord(target).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success(isProspect ? 'Prospecto eliminado' : 'Promotor eliminado');
			setShownModal(null);
			navigate(isProspect ? '/prospects' : '/promoters');
		}).catch(()=>{
			setPrompts(['Hubo un error eliminando el registro. (LCL-1)']);
		}).finally(()=>setLoading(false));
	}

	if(loadError) return <Header text='Error' subtext={loadError} />;

	var name = info ? (isProspect ? info.prospect_name : info.promoter_name) : null;
	var formProspectId = isProspect ? id : info?.prospect_id;

	return <div className='record-view-screen'>
		<div className='info'>
			<div className='data'>
			<Groupper fitted title={isProspect ? 'Datos de prospecto' : 'Datos de promotor'} style={{ margin: 0 }}>
				{(canEdit || canDelete) && (
					<Toolbar stretch>
						{canEdit && <Toolbar.Item text='Asignar' iconName='user-plus' onClick={openAssign} />}
						{isProspect && canConvert && (
							<Toolbar.Item text='Convertir a promotor' iconName='user-tie' onClick={openConvert} />
						)}
						<Toolbar.Dropdown text='Editar' iconName='pen' items={[
							...(canEdit ? [{ text: 'Estatus', iconName: 'flag' as const, onClick: openStatus }] : []),
							...(canEdit ? [{ text: 'Editar nombre', iconName: 'pen' as const, onClick: openEdit }] : []),
							...(canDelete ? [{ text: 'Eliminar', iconName: 'trash' as const, onClick: bindClick(setShownModal, Modals.DELETE) }] : []),
						]} />
					</Toolbar>
				)}
				<Table details fitted striped>
					<Table.Body>
						<Table.Row details data={['ID', id]} />
						<Table.Row details data={['Empresa organizadora', <Placeholder value={name} />]} />
						<Table.Row details data={['Estatus', info ? (
							info.status_name ? <Label color={getStatusColor(info.status_id)}>{info.status_name}</Label> : <Icon name='minus' />
						) : <Placeholder />]} />
						<Table.Row details data={['Asignado a', info ? (
							info.user_assigned_name ? info.user_assigned_name : <Icon name='minus' />
						) : <Placeholder />]} />
						{isProspect && (
							<Table.Row details data={['Origen', info ? (
								info.source_name ? <Label color={info.source_id===ProspectSource.WEB ? 'green' : 'blue'}>{info.source_name}</Label> : <Icon name='minus' />
							) : <Placeholder />]} />
						)}
						<Table.Row details data={['Creado', info ? (
							info.date_created ? formatUnix(info.date_created, 'dd/MMM/yyyy') : <Icon name='minus' />
						) : <Placeholder />]} />
					</Table.Body>
				</Table>
			</Groupper>
			</div>
			{(!info || formProspectId) && (
				<div className='form'>
					{formProspectId ? (
						<PromoterForm prospect_id={formProspectId} mode='admin' canEdit={canEdit} style={{ maxWidth: '100%', margin: 0 }} />
					) : (
						<Header size='small' text='Cargando...' loading />
					)}
				</div>
			)}
		</div>
		<div className='panels'>
			<div className='panel activities'>
				<Activities {...(isProspect ? { prospect_id: id } : { promoter_id: id, include_prospect_id: info?.prospect_id })} />
			</div>
			<div className='panel comments'>
				<Comments {...(isProspect ? { prospect_id: id } : { promoter_id: id, include_prospect_id: info?.prospect_id })} />
			</div>
		</div>
		<Modal open={shownModal===Modals.STATUS} onClose={bindClick(setShownModal, null)} size='tiny'>
			<Modal.Header text='Cambiar estatus' />
			<Modal.Content>
				<Dropdown
					label='Estatus'
					placeholder={statuses ? 'Selecciona un estatus' : 'Cargando...'}
					value={selectedStatus!=null ? String(selectedStatus) : null}
					items={(statuses || []).map(s=>({ text: s.status_name, value: String(s.status_id) }))}
					onValueChange={v=>setSelectedStatus(v!=null && v!=='' ? parseInt(v) : null)}
				/>
				<Message type='error' list={prompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button text='Guardar' color='black' size='small' onClick={saveStatus} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.ASSIGN} onClose={bindClick(setShownModal, null)} size='tiny'>
			<Modal.Header text='Asignar a usuario' />
			<Modal.Content>
				<Dropdown
					label='Usuario'
					placeholder={users ? 'Selecciona un usuario' : 'Cargando...'}
					value={selectedUser!=null ? String(selectedUser) : ''}
					items={[{ text: 'Sin asignar', value: '' }, ...(users || []).map(u=>({ text: `${u.first_name} ${u.last_name}`, value: String(u.user_id) }))]}
					onValueChange={v=>setSelectedUser(v!=null && v!=='' ? parseInt(v) : null)}
				/>
				<Message type='error' list={prompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button text='Guardar' color='black' size='small' onClick={saveAssign} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.EDIT} onClose={bindClick(setShownModal, null)} size='tiny'>
			<Modal.Header text={isProspect ? 'Editar nombre del prospecto' : 'Editar nombre del promotor'} />
			<Modal.Content>
				<Modal.Input label='Nombre' value={editName} onChange={setEditName} maxLength={128} />
				<Message type='error' list={prompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button text='Guardar' color='black' size='small' onClick={saveName} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.DELETE} onClose={bindClick(setShownModal, null)} size='tiny'>
			<Modal.Header text={isProspect ? 'Eliminar prospecto' : 'Eliminar promotor'} />
			<Modal.Content>
				<Header size='small' text='¿Eliminar este registro?' subtext={`Se eliminará "${name}". Esta acción no se puede deshacer.`} />
				<Message type='error' list={prompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button text='Eliminar' color='red' size='small' onClick={removeRecord} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.CONVERT} onClose={bindClick(setShownModal, null)} size='small'>
			<Modal.Header text='Vincular a promotor' />
			<Modal.Content>
				<Header size='small' text='Vincular prospecto a un promotor' subtext={`Selecciona el promotor que se vinculará a "${name}".`} />
				<Message type='error' list={prompts} />
				<div style={{ maxHeight: 340, overflowY: 'auto', marginTop: 10 }}>
					<Table striped selectable fitted style={{ margin: 0 }}>
						<Table.Head>
							<Table.Row>
								<Table.Cell colSpan={3} style={{ padding: 8, position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
									<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
										<Input fluid placeholder='Buscar promotor...' value={linkSearch} onChange={setLinkSearch} iconName='search' style={{ margin: 0, flex: 1 }} />
										<Button color='black' icon iconName='rotate' size='small' loading={refreshing} onClick={refreshPromoters} title='Actualizar promotores desde arema' />
									</div>
								</Table.Cell>
							</Table.Row>
						</Table.Head>
						<Table.Body>
							{!linkResults ? (
								<Table.Row><Table.Cell colSpan={3} centered><Header loading text='Buscando' /></Table.Cell></Table.Row>
							) : linkResults.length===0 ? (
								<Table.Row><Table.Cell colSpan={3} centered>No se encontraron promotores sin vincular</Table.Cell></Table.Row>
							) : linkResults.map(p=>(
								<Table.Row key={p.promoter_id} selectable onClick={()=>setSelectedPromoter(p.promoter_id)} data-active={selectedPromoter===p.promoter_id}>
									<Table.Cell collapsing centered>
										<Icon name={selectedPromoter===p.promoter_id ? 'circle-check' : 'circle'} solid={selectedPromoter===p.promoter_id} color={selectedPromoter===p.promoter_id ? 'var(--color-green)' : 'var(--color-gray-300)'} />
									</Table.Cell>
									<Table.Cell collapsing>{p.promoter_id}</Table.Cell>
									<Table.Cell>{p.promoter_name}</Table.Cell>
								</Table.Row>
							))}
						</Table.Body>
					</Table>
				</div>
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button text='Vincular' color='black' size='small' disabled={selectedPromoter==null} onClick={convert} />
			</Modal.Actions>
		</Modal>
	</div>;
}

export default Promoter;
